/**
 * ESocialFilters Component
 * Inline filter bar: filters stay visible and apply live (no popover).
 */

import React from 'react';
import { TextField } from '@mui/material';
import { InlineFilterBar, InlineSelect } from '../../../../components/common';
import { useFilterState } from '../../../../hooks/useFilterState';
import {
  ESocialEventType,
  ESocialEventStatus,
  ESocialEventFilters,
  ESOCIAL_EVENT_TYPE_SHORT_LABELS,
  ESOCIAL_STATUS_LABELS,
} from '../../../../types/esocial';

interface ESocialFiltersProps {
  filters: ESocialEventFilters;
  onFilterChange: (filters: ESocialEventFilters) => void;
  onClearFilters: () => void;
  loading?: boolean;
  total?: number;
}

interface ESocialLocalFilters {
  eventType: string;
  eventStatus: string;
  dateFrom: string;
  dateTo: string;
}

const EVENT_TYPE_OPTIONS = Object.values(ESocialEventType).map((type) => ({
  value: type,
  label: ESOCIAL_EVENT_TYPE_SHORT_LABELS[type],
}));

const EVENT_STATUS_OPTIONS = Object.values(ESocialEventStatus).map((status) => ({
  value: status,
  label: ESOCIAL_STATUS_LABELS[status],
}));

const defaultValues: ESocialLocalFilters = {
  eventType: '',
  eventStatus: '',
  dateFrom: '',
  dateTo: '',
};

const ESocialFilters: React.FC<ESocialFiltersProps> = ({
  filters,
  onFilterChange,
  onClearFilters,
  loading = false,
  total,
}) => {
  const { filters: localFilters, setFilter, clearAll, activeChips } = useFilterState(
    defaultValues,
    {
      onFilter: (f) => {
        onFilterChange({
          ...filters,
          eventType: f.eventType ? (f.eventType as ESocialEventType) : undefined,
          eventStatus: f.eventStatus ? (f.eventStatus as ESocialEventStatus) : undefined,
          dateFrom: f.dateFrom || undefined,
          dateTo: f.dateTo || undefined,
        });
      },
      live: true,
      labels: {
        eventType: 'Tipo',
        eventStatus: 'Status',
        dateFrom: 'Data Inicial',
        dateTo: 'Data Final',
      },
      formatValue: {
        eventType: (v: string) => ESOCIAL_EVENT_TYPE_SHORT_LABELS[v as ESocialEventType] ?? v,
        eventStatus: (v: string) => ESOCIAL_STATUS_LABELS[v as ESocialEventStatus] ?? v,
      },
    },
  );

  const handleSearchChange = (value: string) => {
    onFilterChange({ ...filters, search: value || undefined });
  };

  const handleClearAll = () => {
    clearAll();
    onClearFilters();
  };

  return (
    <InlineFilterBar
      searchPlaceholder="Buscar..."
      searchValue={filters.search ?? ''}
      onSearchChange={handleSearchChange}
      resultCount={total}
      resultNoun="eventos"
      chips={activeChips}
      onClearAll={handleClearAll}
      loading={loading}
    >
      <InlineSelect
        label="Tipo"
        value={localFilters.eventType}
        options={EVENT_TYPE_OPTIONS}
        onChange={(v) => setFilter('eventType', v)}
        emptyLabel="Todos"
        minWidth={170}
      />
      <InlineSelect
        label="Status"
        value={localFilters.eventStatus}
        options={EVENT_STATUS_OPTIONS}
        onChange={(v) => setFilter('eventStatus', v)}
        emptyLabel="Todos"
        minWidth={150}
      />
      <TextField
        size="small"
        type="date"
        label="Data Inicial"
        value={localFilters.dateFrom}
        onChange={(e) => setFilter('dateFrom', e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 160 }}
      />
      <TextField
        size="small"
        type="date"
        label="Data Final"
        value={localFilters.dateTo}
        onChange={(e) => setFilter('dateTo', e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 160 }}
      />
    </InlineFilterBar>
  );
};

export default ESocialFilters;
